import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Form,
  InputGroup,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";

const isMissing = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "—";

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

export default function AdminOrderEmailData() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { event_id: selectedEventId = "" } = useParams();
  const [authorized, setAuthorized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [resendStates, setResendStates] = useState({});

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const snapshot = await fires
        .collection("orders")
        .orderBy("timestamp", "desc")
        .get();

      const incompleteOrders = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter(
          (order) => isMissing(order.emailId) || isMissing(order.emailStatus)
        );

      setOrders(incompleteOrders);
    } catch (err) {
      console.error("Error loading order email data:", err);
      setError(`Failed to load orders: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    const checkAccess = async () => {
      if (!currentUser) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const adminDoc = await fires
          .collection("admin")
          .doc(currentUser.uid)
          .get();
        const roles = adminDoc.exists ? adminDoc.data().roles || [] : [];

        if (!roles.includes("data")) {
          if (active) {
            setError("Access denied. The data role is required.");
            setCheckingAccess(false);
          }
          return;
        }

        if (active) {
          setAuthorized(true);
          setCheckingAccess(false);
        }
      } catch (err) {
        console.error("Error checking data-role access:", err);
        if (active) {
          setError("Unable to verify data-role access.");
          setCheckingAccess(false);
        }
      }
    };

    checkAccess();
    return () => {
      active = false;
    };
  }, [currentUser, navigate]);

  useEffect(() => {
    if (authorized) loadOrders();
  }, [authorized, loadOrders]);

  const filteredOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const eventOrders = selectedEventId
      ? orders.filter((order) => order.eventId === selectedEventId)
      : orders;

    if (!query) return eventOrders;

    return eventOrders.filter((order) =>
      [
        order.id,
        order.uniqueId,
        order.userEmail,
        order.title,
        order.eventId,
        order.emailId,
        order.emailStatus,
      ].some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [orders, searchTerm, selectedEventId]);

  const eventOptions = useMemo(() => {
    const events = new Map();

    orders.forEach((order) => {
      if (order.eventId) {
        events.set(
          order.eventId,
          order.title || order.eventTitle || order.eventId
        );
      }
    });

    return Array.from(events, ([id, title]) => ({ id, title })).sort((a, b) =>
      a.title.localeCompare(b.title)
    );
  }, [orders]);

  const resendOrderEmail = async (order) => {
    setResendStates((states) => ({
      ...states,
      [order.id]: { sending: true, error: "", success: false },
    }));

    try {
      const params = new URLSearchParams({
        order_id: order.id,
        user_name: order.userName || "",
      });
      const response = await fetch(
        `https://v2eventsapisendorderemail-650318705789.us-central1.run.app?${params.toString()}`
      );

      if (!response.ok) {
        let message = `Request failed (${response.status})`;
        try {
          const responseBody = await response.json();
          message = responseBody.message || responseBody.error || message;
        } catch {
          // The API did not return JSON, so keep the HTTP status message.
        }
        throw new Error(message);
      }

      setResendStates((states) => ({
        ...states,
        [order.id]: { sending: false, error: "", success: true },
      }));
    } catch (err) {
      console.error(`Error resending email for order ${order.id}:`, err);
      setResendStates((states) => ({
        ...states,
        [order.id]: {
          sending: false,
          error: err.message || "Unable to resend email.",
          success: false,
        },
      }));
    }
  };

  if (checkingAccess) {
    return (
      <Container className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Checking access...</span>
        </Spinner>
      </Container>
    );
  }

  if (!authorized) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-lg-5">
      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h4 className="mb-1">Order Email Data Gaps</h4>
            <div className="text-muted">
              Orders missing an email ID or email status ({orders.length})
            </div>
          </div>
          <Button variant="dark" onClick={loadOrders} disabled={loading}>
            {loading ? "Loading..." : "Reload"}
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <div className="d-flex flex-column flex-lg-row gap-3 mb-3">
            <Form.Select
              value={selectedEventId}
              onChange={(event) => {
                const eventId = event.target.value;
                navigate(
                  eventId
                    ? `/admin-order-email-data/${encodeURIComponent(eventId)}`
                    : "/admin-order-email-data"
                );
              }}
              aria-label="Filter orders by event"
              style={{ maxWidth: "420px" }}
            >
              <option value="">All events</option>
              {eventOptions.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title} ({event.id})
                </option>
              ))}
            </Form.Select>

            <InputGroup>
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Order ID, customer email, event, email ID, or status"
                aria-label="Search orders"
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading orders...</span>
              </Spinner>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Created</th>
                    <th>Customer</th>
                    <th>Event</th>
                    <th>Email ID</th>
                    <th>Email Status</th>
                    <th>Missing</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const missingEmailId = isMissing(order.emailId);
                    const missingEmailStatus = isMissing(order.emailStatus);
                    const resendState = resendStates[order.id] || {};

                    return (
                      <tr key={order.id}>
                        <td>
                          <a href={`/admin-order-details/${order.id}`}>
                            {order.uniqueId || order.id}
                          </a>
                        </td>
                        <td>{formatTimestamp(order.timestamp)}</td>
                        <td>{order.userEmail || "—"}</td>
                        <td>{order.title || order.eventId || "—"}</td>
                        <td>{missingEmailId ? "—" : String(order.emailId)}</td>
                        <td>
                          {missingEmailStatus
                            ? "—"
                            : String(order.emailStatus)}
                        </td>
                        <td>
                          {missingEmailId && (
                            <Badge bg="danger" className="me-1">
                              emailId
                            </Badge>
                          )}
                          {missingEmailStatus && (
                            <Badge bg="warning" text="dark">
                              emailStatus
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant={resendState.success ? "success" : "primary"}
                            disabled={resendState.sending}
                            onClick={() => resendOrderEmail(order)}
                          >
                            {resendState.sending
                              ? "Sending..."
                              : resendState.success
                              ? "Sent"
                              : "Resend"}
                          </Button>
                          {resendState.error && (
                            <div className="text-danger small mt-1">
                              {resendState.error}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No matching orders with missing email data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
